/**
 * brandImportColumns: the one list of BRAND import columns.
 * ───────────────────────────────────────────────────────────────────────────
 * Every reader of a brand workbook takes its columns from here:
 *
 *   - brandImport.js turns whatever header a sheet carries into the field key
 *     it means (canonicalHeader), so "Price (UGX)" and "price_ugx" both work.
 *   - brandImportTemplate.js writes these labels, hints and formulas into the
 *     workbook the Template button downloads.
 *   - The China Town workbook in Drive, which is the blueprint every brand
 *     sheet follows from 26 Sep 2026, was built from this same list.
 *
 * WHAT IS HERE, AND WHAT MOVED. The roles, the sheet names, the Status words,
 * the section columns, header resolution, the column-letter maths and both
 * self-filling formulas now live in catalogueImportColumns.js, shared with
 * healthcare and with whichever vertical gets an importer next. What stays
 * here is the product column ORDER and the five groups of columns only a brand
 * has: the lead time, the bulk price, the three trade tiers and the order-flow
 * override. Everything this file exports is still exported, so nothing that
 * imported from it had to change.
 *
 * Headers are Title Case words. The snake_case keys used before still import,
 * so an older sheet keeps working.
 */
import {
  IDENTITY_COLUMNS,
  MEDIA_COLUMNS,
  MERCHANDISING_COLUMNS,
  NOTES_COLUMN,
  PAYMENT_COLUMN,
  PRICING_COLUMNS,
  ROLE,
  SECTION_COLUMNS,
  STOCK_COLUMNS,
  col,
  createHeaderResolver,
  makeProductRange,
} from "./catalogueImportColumns.js";

export {
  NAMES,
  PLACEHOLDER,
  PRODUCTS_SHEET,
  PRODUCT_FORMULAS,
  ROLE,
  ROLE_LABEL,
  SECTIONS_SHEET,
  SECTION_COLUMNS,
  SECTION_FORMULAS,
  SPEC_PREFIX,
  STATUS,
  columnLetter,
  columnLetters,
  headerKey,
  specHeader,
} from "./catalogueImportColumns.js";

/**
 * Product columns in the order a person fills them in: who it is, what it
 * costs, whether it can be bought, what it looks like, then the commercial
 * fine print. The order is the China Town blueprint's and must not be
 * reshuffled: the workbook's formulas address columns by letter.
 */
export const PRODUCT_COLUMNS = [
  ...IDENTITY_COLUMNS,
  ...PRICING_COLUMNS,
  ...STOCK_COLUMNS,
  ...MEDIA_COLUMNS,
  ...MERCHANDISING_COLUMNS,
  /* Brand-only from here down. A pharmacy has no trade tiers (the healthcare
     pricing phase is not tier-aware, see models/healthcareSectionProductModel.js)
     and no order-flow override, so these columns are deliberately NOT in the
     shared groups above. */
  col("lead_time_days", "Lead Time (Days)", ROLE.OPTIONAL, "Days to fulfil, if different from the brand default."),
  col("bulk_price_ugx", "Bulk Price (UGX)", ROLE.OPTIONAL, "Price per unit at bulk quantity, if offered."),
  col("tier_price_retailer", "Retailer Price (UGX)", ROLE.OPTIONAL, "Price for retail buyers, if tiered."),
  col("tier_price_wholesaler", "Wholesaler Price (UGX)", ROLE.OPTIONAL, "Price for wholesale buyers, if tiered."),
  col("tier_price_healthcare", "Healthcare Price (UGX)", ROLE.OPTIONAL, "Price for healthcare buyers, if tiered."),
  col("order_flow_override", "Order Flow", ROLE.OPTIONAL, "Blank follows the brand. Or STANDARD, BRANDED_PACKAGING, QUOTE."),
  PAYMENT_COLUMN,
  NOTES_COLUMN,
];

const resolver = createHeaderResolver({
  sections: SECTION_COLUMNS,
  products: PRODUCT_COLUMNS,
});

export const COLUMNS_BY_KIND = resolver.COLUMNS_BY_KIND;

/** The field key a header means on a brand sheet. Unknown headers pass through. */
export const canonicalHeader = resolver.canonicalHeader;

/** The Title Case label for a key, for messages an operator reads. */
export const labelOf = resolver.labelOf;

/** Builds P(key) for SECTION_FORMULAS: a product column over rows first..last. */
export const productRange = makeProductRange(PRODUCT_COLUMNS);
